import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tag, Filter, Edit, Plus } from 'lucide-react';
import StatusChip from '../components/StatusChip';
import EditDBADialog from '../components/EditDBADialog';
import AddDBADialog from '../components/AddDBADialog';
import { api } from '../utils/api';
import { formatDate, getDaysUntil } from '../utils/helpers';

const DBARegister = ({ user }) => {
  const [dbas, setDbas] = useState([]);
  const [filteredDbas, setFilteredDbas] = useState([]);
  const [stateFilter, setStateFilter] = useState('all');
  const [sortBy, setSortBy] = useState('state');
  const [loading, setLoading] = useState(true);
  const [editingDba, setEditingDba] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  useEffect(() => {
    loadDbas();
    
    // Refresh data when window/tab regains focus
    const handleFocus = () => {
      loadDbas();
    };
    
    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  useEffect(() => {
    applyFilters();
  }, [stateFilter, sortBy, dbas]);

  const loadDbas = async () => {
    try {
      setLoading(true);
      // Load DBAs directly from /api/dbas endpoint (SSOT for DBA data)
      const response = await api.getDbas();
      const dbasData = response.dbas || [];
      
      // Filter to show ONLY regular DBAs (exclude Foreign Corporations)
      // Foreign Corporations should appear on Ancillary Registrations page instead
      const regularDbas = dbasData.filter(dba => {
        return dba.registration_type === 'DBA' || !dba.registration_type;
      });
      
      console.log('[DBA Register] Loaded DBAs:', regularDbas.length, '(filtered from', dbasData.length, 'total)');
      
      // Sort by state and DBA name
      regularDbas.sort((a, b) => {
        if (a.state !== b.state) return a.state.localeCompare(b.state);
        return a.dba_name.localeCompare(b.dba_name);
      });
      
      setDbas(regularDbas);
      setFilteredDbas(regularDbas);
    } catch (error) {
      console.error('Failed to load DBAs:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...dbas];
    
    // Apply state filter
    if (stateFilter !== 'all') {
      filtered = filtered.filter(d => d.state === stateFilter);
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      if (sortBy === 'state') {
        return a.state.localeCompare(b.state);
      } else if (sortBy === 'dba_name') {
        return a.dba_name.localeCompare(b.dba_name);
      } else if (sortBy === 'expiration_date') {
        const dateA = a.expiration_date ? new Date(a.expiration_date) : new Date('9999-12-31');
        const dateB = b.expiration_date ? new Date(b.expiration_date) : new Date('9999-12-31');
        return dateA - dateB;
      }
      return 0;
    });
    
    setFilteredDbas(filtered);
  };

  const handleEdit = (dba) => {
    setEditingDba(dba);
    setEditDialogOpen(true);
  };

  const handleEditSuccess = () => {
    loadDbas();
  };

  const states = [...new Set(dbas.map(d => d.state))].sort();

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="space-y-6 fade-in" data-testid="dba-register-container">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight">DBA Register</h1>
          <p className="text-muted-foreground mt-2">Doing Business As names registered across all states</p>
        </div>
        <Button onClick={() => setAddDialogOpen(true)} data-testid="add-dba-button" className="w-full sm:w-auto">
          <Plus className="h-4 w-4 mr-2" />
          Add DBA
        </Button>
      </div>

      <Card data-testid="filter-bar">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <Filter className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            
            {/* State Filter */}
            <Select value={stateFilter} onValueChange={setStateFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Filter by state" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All States</SelectItem>
                {states.map(state => (
                  <SelectItem key={state} value={state}>{state}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Sort By */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="state">Sort by State</SelectItem>
                <SelectItem value="dba_name">Sort by DBA Name</SelectItem>
                <SelectItem value="expiration_date">Sort by Expiration</SelectItem>
              </SelectContent>
            </Select>
            
            <div className="text-sm text-muted-foreground sm:ml-auto">
              Showing {filteredDbas.length} of {dbas.length} DBAs
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="overflow-x-auto" data-testid="dbas-table">
        <table className="w-full min-w-[900px]">
          <thead className="table-header">
            <tr className="border-b border-border">
              <th className="table-cell text-left font-medium text-muted-foreground">DBA ID</th>
              <th className="table-cell text-left font-medium text-muted-foreground">DBA Name</th>
              <th className="table-cell text-left font-medium text-muted-foreground">State</th>
              <th className="table-cell text-left font-medium text-muted-foreground">County</th>
              <th className="table-cell text-left font-medium text-muted-foreground">Registration Date</th>
              <th className="table-cell text-left font-medium text-muted-foreground">Expiration</th>
              <th className="table-cell text-left font-medium text-muted-foreground">Days Until</th>
              <th className="table-cell text-left font-medium text-muted-foreground">Status</th>
              <th className="table-cell text-left font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredDbas.map((dba) => {
              const daysUntil = dba.expiration_date ? getDaysUntil(dba.expiration_date) : null;
              return (
                <tr key={dba.dba_id} className="table-row border-b border-border" data-testid={`dba-row-${dba.dba_id}`}>
                  <td className="table-cell table-cell-mono">{dba.dba_id}</td>
                  <td className="table-cell font-medium">{dba.dba_name}</td>
                  <td className="table-cell">{dba.state}</td>
                  <td className="table-cell">{dba.county || 'N/A'}</td>
                  <td className="table-cell tabular-nums">{formatDate(dba.registration_date)}</td>
                  <td className="table-cell tabular-nums">{formatDate(dba.expiration_date)}</td>
                  <td className="table-cell">
                    {daysUntil !== null ? (
                      <span className={`font-medium ${
                        daysUntil < 0 ? 'text-rose-600' : 
                        daysUntil <= 60 ? 'text-amber-600' : 
                        'text-muted-foreground'
                      }`}>
                        {daysUntil < 0 ? `${Math.abs(daysUntil)} days ago` : `${daysUntil} days`}
                      </span>
                    ) : 'N/A'}
                  </td>
                  <td className="table-cell">
                    <StatusChip status={dba.status} />
                  </td>
                  <td className="table-cell">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(dba)}
                      data-testid={`edit-dba-${dba.dba_id}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filteredDbas.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            No DBAs match the current filters
          </div>
        )}
      </div>

      <EditDBADialog
        dba={editingDba}
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSuccess={handleEditSuccess}
      />

      <AddDBADialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSuccess={handleEditSuccess}
      />
    </div>
  );
};

export default DBARegister;
